import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase.js';

/** Deep-link target the native wrapper listens for (see native/deepLinks.ts). */
export const NATIVE_AUTH_CALLBACK = 'ge.fluent.app://auth-callback';

/**
 * Where Google sends people back to on the web. Must be listed as an
 * "Authorized redirect URI" on the Google Cloud web client
 * (project fluent-ge-2026): https://app.fluent.ge/auth/google
 */
export const GOOGLE_CALLBACK_PATH = '/auth/google';

/** The only origin whose callback is registered with Google. */
const OWN_DOMAIN_HOST = 'app.fluent.ge';

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

/** Which screen the Google button was on — errors go back there. */
export type GoogleFlow = 'login' | 'signup';

/**
 * Google sign-in that works on the web AND inside the native wrapper.
 *
 * WEB, ON app.fluent.ge — Google comes straight back to us. Supabase's own
 * Google redirect goes through hbdxrqopckpfzbyjtfcs.supabase.co, and Google
 * names that address on its sign-in page ("to continue to …supabase.co").
 * Asking Google for an ID token with our own callback makes it say
 * app.fluent.ge instead (and fluent.ge once the brand is verified); the
 * callback page then hands the token to Supabase (signInWithIdToken). Free
 * — a Supabase custom domain would need the $35/month plan.
 *
 * ELSEWHERE (localhost, preview deploys) — Supabase's own redirect, because
 * only app.fluent.ge's callback is registered with Google.
 *
 * NATIVE — Google refuses OAuth inside embedded webviews
 * ("disallowed_useragent"), so we open the SYSTEM browser (Chrome Custom
 * Tab / SFSafariViewController) with skipBrowserRedirect and finish the flow
 * via the ge.fluent.app:// deep link. The callback carries tokens in the URL
 * fragment and deepLinks.ts calls setSession with them.
 *
 * Returns an error message to show, or null on success/handoff.
 */
export async function signInWithGoogle(flow: GoogleFlow): Promise<string | null> {
  if (Capacitor.isNativePlatform()) {
    const { Browser } = await import('@capacitor/browser');
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: NATIVE_AUTH_CALLBACK, skipBrowserRedirect: true },
    });
    if (error) return error.message;
    if (!data?.url) return 'auth_url_missing';
    await Browser.open({ url: data.url });
    return null;
  }

  const clientId = ownDomainClientId(window.location.hostname);
  if (clientId) {
    const nonce = randomToken();
    window.location.assign(
      googleAuthUrl({
        clientId,
        origin: window.location.origin,
        state: `${flow}.${nonce}`,
        hashedNonce: await sha256Hex(nonce),
      }),
    );
    return null;
  }

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${window.location.origin}/` },
  });
  return error ? error.message : null;
}

/** The Google web client id, but only where its callback is registered. */
export function ownDomainClientId(hostname: string): string | null {
  const id = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  return id && hostname === OWN_DOMAIN_HOST ? id : null;
}

/**
 * Google's OpenID Connect sign-in URL, asking for an ID token in the URL
 * fragment.
 *
 * The nonce: Google writes the HASHED nonce into the token, Supabase is
 * given the RAW one and checks they match. The raw nonce rides back in
 * `state` instead of localStorage — an installed iPhone app finishes the
 * redirect in a Safari sheet that can't see the app's storage (the reason
 * lib/supabase.ts uses the implicit flow, 2026-04-28).
 */
export function googleAuthUrl(opts: {
  clientId: string;
  origin: string;
  state: string;
  hashedNonce: string;
}): string {
  const params = new URLSearchParams({
    client_id: opts.clientId,
    redirect_uri: `${opts.origin}${GOOGLE_CALLBACK_PATH}`,
    response_type: 'id_token',
    scope: 'openid email profile',
    nonce: opts.hashedNonce,
    state: opts.state,
  });
  return `${GOOGLE_AUTH_URL}?${params.toString()}`;
}

export type GoogleCallbackResult =
  | { flow: GoogleFlow; idToken: string; nonce: string }
  | { flow: GoogleFlow; error: string };

/** Reads what Google put in the callback's URL fragment. */
export function parseGoogleCallback(hash: string): GoogleCallbackResult {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const state = params.get('state') ?? '';
  const dot = state.indexOf('.');
  const head = dot > 0 ? state.slice(0, dot) : '';
  const flow: GoogleFlow = head === 'signup' ? 'signup' : 'login';
  const nonce = dot > 0 ? state.slice(dot + 1) : '';

  const error = params.get('error');
  if (error) return { flow, error };
  const idToken = params.get('id_token');
  if (!idToken || !nonce || (head !== 'login' && head !== 'signup')) {
    return { flow, error: 'invalid_google_response' };
  }
  return { flow, idToken, nonce };
}

/**
 * Signs in with what came back from Google. Returns where to go next:
 * home on success, or back to the screen the button was on.
 */
export async function finishGoogleSignIn(cb: GoogleCallbackResult): Promise<string> {
  const back = `/${cb.flow}`;
  if ('error' in cb) {
    // They closed Google's page or pressed Cancel — nothing went wrong.
    if (cb.error === 'access_denied') return back;
    return `${back}?error=${encodeURIComponent(cb.error)}`;
  }
  const { error } = await supabase.auth.signInWithIdToken({
    provider: 'google',
    token: cb.idToken,
    nonce: cb.nonce,
  });
  if (error) return `${back}?error_description=${encodeURIComponent(error.message)}`;
  return '/';
}

function randomToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function sha256Hex(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}
